import {
    Card,
    COMPONENT_HEIGHTS,
    CYCLE_PAGE_LAYOUT,
    Colors,
    Container,
    defaultAnimation,
    FIXED_ICON_SIZES,
    Heading,
    Icon,
    MultiSelectOutlined,
    SearchBar,
    Text,
    type ComponentSize,
    type OptionProps,
} from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';

import { CycleEvaluationContent } from './CycleEvaluationContent/CycleEvaluationContent';
import {
    CycleEvaluationTabs,
    getCycleEvaluationTabLabelKey,
} from './CycleEvaluationTabs/CycleEvaluationTabs';
import { ObjectivePeriods } from './ObjectivePeriods/ObjectivePeriods';
import { ProgressTracking } from './ProgressTracking/ProgressTracking';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationDashboardTab } from '@/types/cycle-evaluation/CycleEvaluationDashboardTab';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { useAmelioTranslations } from '@/hooks';
import { useHandleClickOutside } from '@/hooks/utils/useHandleClickOutside';
import { useWindowDimensions } from '@/hooks/utils/useWindowsDimensions';
import { scrollPageToTop } from '@/navigation/ScrollToTop';
import {
    cycleCardRadius,
    cycleCompactControlPadding,
    cyclePageSubtitleText,
    cyclePageTitleText,
    cycleSectionIconFrame,
    cycleSectionIconSize,
} from '@/utils/cycleStyles';
import { TOOLBAR_CONTROL_TEXT_SIZE } from '@/utils/toolbarStyles';

const SEARCH_INPUT_ID = 'cycle-evaluation-search-input';

const DEFAULT_TAB: CycleEvaluationDashboardTab = 'evaluationPeriods';

/**
 * The tab to open on: the one asked for when the reader may see it, else the first they may.
 * `availableTabs` is never empty in practice -- the route only admits readers with at least one
 * scope -- but the default keeps the dashboard renderable if it ever were.
 */
const resolveInitialTab = (
    requestedTab: CycleEvaluationDashboardTab,
    availableTabs: CycleEvaluationDashboardTab[],
): CycleEvaluationDashboardTab =>
    availableTabs.includes(requestedTab) ? requestedTab : (availableTabs[0] ?? DEFAULT_TAB);

interface FilterControlLayout {
    size: ComponentSize;
    triggerClassName: string;
    iconSize?: number;
    rowGap: string;
}

const COMPACT_FILTER_CONTROL_LAYOUT: FilterControlLayout = {
    size: 'sm',
    triggerClassName: cycleCompactControlPadding,
    iconSize: FIXED_ICON_SIZES.xs,
    rowGap: 'gap-[4px]',
};

const WIDE_FILTER_CONTROL_LAYOUT: FilterControlLayout = {
    size: 'md',
    triggerClassName: '',
    rowGap: 'gap-3',
};

const SEARCH_TOGGLE_TRIGGER_CLASSES = `border border-base-300 bg-white hover:border-primary ${defaultAnimation} focus:ring-primary flex cursor-pointer justify-between gap-2 rounded-lg focus:ring-1 focus:outline-none items-center ${COMPACT_FILTER_CONTROL_LAYOUT.triggerClassName} ${COMPONENT_HEIGHTS.sm}`;

export interface DashboardProps {
    cycleEvaluationGroups?: CycleEvaluationGroup[];
    cycleEvaluations?: CycleEvaluation[];
    /** Unfiltered evaluation list, used to resolve the template names of each cycle's children. */
    allCycleEvaluations?: CycleEvaluation[];
    isLoading: boolean;
    actionMenuItems: (cycleEvaluation: CycleEvaluation) => OptionProps[];
    groupActionMenuItems: (group: CycleEvaluationGroup) => OptionProps[];
    isUpdating: boolean;
    currentEvaluation?: CycleEvaluation;
    isRefetching: boolean;
    searchText: string;
    setSearchText: (searchText: string) => void;
    statusFilter: string[];
    statusFilterOptions: OptionProps[];
    onStatusFilterChange: (value: string) => void;
    typeFilter: string[];
    typeFilterOptions: OptionProps[];
    onTypeFilterChange: (value: string) => void;
    onCreateCycle: () => void;
    onEditEvaluation: (cycleEvaluation: CycleEvaluation) => void;
    onEditGroup: (group: CycleEvaluationGroup) => void;
    /** The tab to open on, when the caller arrived asking for one (see `DashboardPage`); the
     * evaluation periods tab otherwise. */
    initialTab?: CycleEvaluationDashboardTab;
    /** The tabs the reader may open (see `getAvailableDashboardTabs`); every tab when omitted. */
    availableTabs?: CycleEvaluationDashboardTab[];
    currentPage?: number;
    totalCycleCount?: number;
    onCyclePageChange?: (page: number) => void;
}

export const Dashboard = ({
    cycleEvaluationGroups,
    cycleEvaluations,
    allCycleEvaluations,
    isLoading,
    actionMenuItems,
    groupActionMenuItems,
    isUpdating,
    currentEvaluation,
    isRefetching,
    searchText,
    setSearchText,
    statusFilter,
    statusFilterOptions,
    onStatusFilterChange,
    typeFilter,
    typeFilterOptions,
    onTypeFilterChange,
    onCreateCycle,
    onEditEvaluation,
    onEditGroup,
    initialTab = DEFAULT_TAB,
    availableTabs,
    currentPage,
    totalCycleCount,
    onCyclePageChange,
}: DashboardProps) => {
    const { t } = useAmelioTranslations();
    const { isMobile } = useWindowDimensions();

    const [activeTab, setActiveTab] = useState<CycleEvaluationDashboardTab>(() =>
        availableTabs ? resolveInitialTab(initialTab, availableTabs) : initialTab,
    );
    const selectTab = (tab: CycleEvaluationDashboardTab) => {
        setActiveTab(tab);
        scrollPageToTop();
    };
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const filterBlockRef = useRef<HTMLDivElement>(null);

    useHandleClickOutside({
        clicked: isSearchOpen && searchText.length === 0,
        dropdownContainerRef: filterBlockRef,
        setButtonClicked: setIsSearchOpen,
    });

    // The tabs a reader may open are derived from their permissions and from the feature flags,
    // both of which read as "no" until their requests answer. The initial tab above is therefore
    // settled against a list that is often still empty, and it would stay settled: a deep link to
    // the objective periods was lost, and an objectives manager landed on the evaluation-periods
    // panel -- the one tab they may not open -- with nothing selected in the bar.
    //
    // So the tab is re-settled once the list is known, and only while the current one is not in
    // it. A reader who has since picked a tab of their own is never overridden, because their
    // choice is necessarily one of the available ones.
    useEffect(() => {
        if (!availableTabs?.length || availableTabs.includes(activeTab)) {
            return;
        }

        setActiveTab(resolveInitialTab(initialTab, availableTabs));
    }, [availableTabs, activeTab, initialTab]);

    useEffect(() => {
        if (!isSearchOpen) {
            return;
        }

        filterBlockRef.current?.querySelector<HTMLInputElement>(`#${SEARCH_INPUT_ID}`)?.focus();
    }, [isSearchOpen]);

    const isEvaluationPeriodsTab = activeTab === 'evaluationPeriods';
    const isProgressTrackingTab = activeTab === 'progressTracking';
    const isObjectivePeriodsTab = activeTab === 'objectivePeriods';
    const showSearchBar = !isMobile || isSearchOpen;
    const filterControlLayout = isMobile
        ? COMPACT_FILTER_CONTROL_LAYOUT
        : WIDE_FILTER_CONTROL_LAYOUT;

    const hasActiveFilters = statusFilter.length > 0 || typeFilter.length > 0;

    // The library stylesheet loads AFTER the app's, so an unprefixed library utility beats a `md:`
    // variant declared here — hence the bare `Container`, and responsive pairs whose two sides
    // never share a media query.
    return (
        <Container
            className={`${CYCLE_PAGE_LAYOUT.maxWidth} pb-[48px] max-md:px-[32px] max-md:pt-[48px] md:pt-[71px] md:max-xl:px-[64px] xl:px-[80px]`}
            dataTestId={'cycle-evaluation-dashboard'}
        >
            <div className={'relative flex flex-col'}>
                <div className={'flex flex-col gap-[11px]'}>
                    <Heading
                        level={1}
                        weight={'semibold'}
                        className={cyclePageTitleText}
                    >
                        {t('cycleEvaluationDashboard.page.title')}
                    </Heading>

                    <Text
                        color={Colors.BASE_500}
                        className={cyclePageSubtitleText}
                    >
                        {t('cycleEvaluationDashboard.page.subtitle')}
                    </Text>
                </div>

                <CycleEvaluationTabs
                    activeTab={activeTab}
                    onTabChange={selectTab}
                    tabs={availableTabs}
                    className={'mt-[34px]'}
                />

                <div
                    role={'tabpanel'}
                    aria-label={t(getCycleEvaluationTabLabelKey(activeTab))}
                    className={'mt-[24px] flex flex-col'}
                >
                    {isEvaluationPeriodsTab && (
                        <Card
                            padding={'none'}
                            className={`relative flex flex-col justify-center max-md:gap-[16px] max-md:p-[16px] md:gap-[24px] md:p-[48px] ${cycleCardRadius}`}
                            borderColor={Colors.WHITE}
                        >
                            <div
                                className={
                                    'flex w-full justify-between gap-[24px] max-2xl:flex-col max-2xl:items-start 2xl:flex-row 2xl:items-start'
                                }
                            >
                                <div className={'flex min-w-0 items-start gap-[12px]'}>
                                    <div
                                        className={`bg-base-50 flex shrink-0 items-center justify-center shadow-sm ${cycleSectionIconFrame} rounded-lg`}
                                        aria-hidden={'true'}
                                    >
                                        <Icon
                                            name={'edit-clipboard-tick'}
                                            className={cycleSectionIconSize}
                                            color={Colors.STEP_RAIL_VIOLET}
                                        />
                                    </div>

                                    <div className={'flex min-w-0 flex-col gap-1.5'}>
                                        <Heading
                                            level={3}
                                            weight={'semibold'}
                                        >
                                            {t('cycleEvaluationDashboard.evaluationCycles.title')}
                                        </Heading>

                                        <Text
                                            size={'small'}
                                            color={Colors.BASE_500}
                                            className={'wrap-break-word'}
                                        >
                                            {t(
                                                'cycleEvaluationDashboard.evaluationCycles.description',
                                            )}
                                        </Text>
                                    </div>
                                </div>
                                <div
                                    ref={filterBlockRef}
                                    className={`flex flex-wrap items-center justify-start ${filterControlLayout.rowGap} 2xl:shrink-0 2xl:flex-nowrap 2xl:justify-end`}
                                    data-testid={'cycle-evaluation-filters'}
                                >
                                    <MultiSelectOutlined
                                        options={statusFilterOptions}
                                        values={statusFilter}
                                        onChange={onStatusFilterChange}
                                        placeholder={t('cycleEvaluationDashboard.filters.status')}
                                        icon={
                                            <Icon
                                                name={'essential-status'}
                                                size={filterControlLayout.iconSize}
                                                color={Colors.BASE_400}
                                            />
                                        }
                                        size={filterControlLayout.size}
                                        textSize={TOOLBAR_CONTROL_TEXT_SIZE}
                                        className={filterControlLayout.triggerClassName}
                                        sort={'none'}
                                        ariaLabel={t('cycleEvaluationDashboard.filters.status')}
                                        dataTestId={'cycle-evaluation-status-filter'}
                                    />

                                    <MultiSelectOutlined
                                        options={typeFilterOptions}
                                        values={typeFilter}
                                        onChange={onTypeFilterChange}
                                        placeholder={t(
                                            'cycleEvaluationDashboard.filters.evaluationType',
                                        )}
                                        icon={
                                            <Icon
                                                name={'settings-category'}
                                                size={filterControlLayout.iconSize}
                                                color={Colors.BASE_400}
                                            />
                                        }
                                        size={filterControlLayout.size}
                                        textSize={TOOLBAR_CONTROL_TEXT_SIZE}
                                        className={filterControlLayout.triggerClassName}
                                        sort={'none'}
                                        ariaLabel={t(
                                            'cycleEvaluationDashboard.filters.evaluationType',
                                        )}
                                        dataTestId={'cycle-evaluation-type-filter'}
                                    />

                                    {showSearchBar ? (
                                        <SearchBar
                                            id={SEARCH_INPUT_ID}
                                            size={filterControlLayout.size}
                                            textSize={TOOLBAR_CONTROL_TEXT_SIZE}
                                            placeholder={t(
                                                'cycleEvaluationDashboard.evaluationCycles.searchPlaceholder',
                                            )}
                                            value={searchText}
                                            onChange={setSearchText}
                                        />
                                    ) : (
                                        <button
                                            type={'button'}
                                            className={SEARCH_TOGGLE_TRIGGER_CLASSES}
                                            onClick={() => setIsSearchOpen(true)}
                                            aria-label={t(
                                                'cycleEvaluationDashboard.evaluationCycles.searchPlaceholder',
                                            )}
                                            data-testid={'cycle-evaluation-search-toggle'}
                                        >
                                            <Icon
                                                name={'search-search-normal-1'}
                                                size={COMPACT_FILTER_CONTROL_LAYOUT.iconSize}
                                                color={Colors.BASE_400}
                                            />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <CycleEvaluationContent
                                isLoading={isLoading}
                                cycleEvaluationGroups={cycleEvaluationGroups}
                                cycleEvaluations={cycleEvaluations}
                                allCycleEvaluations={allCycleEvaluations}
                                actionMenuItems={actionMenuItems}
                                groupActionMenuItems={groupActionMenuItems}
                                isUpdating={isUpdating}
                                currentEvaluation={currentEvaluation}
                                isRefetching={isRefetching}
                                searchText={searchText}
                                hasActiveFilters={hasActiveFilters}
                                onCreateCycle={onCreateCycle}
                                onEditEvaluation={onEditEvaluation}
                                onEditGroup={onEditGroup}
                                currentPage={currentPage}
                                totalCycleCount={totalCycleCount}
                                onCyclePageChange={onCyclePageChange}
                            />
                        </Card>
                    )}

                    {isProgressTrackingTab && <ProgressTracking />}

                    {isObjectivePeriodsTab && <ObjectivePeriods />}
                </div>
            </div>
        </Container>
    );
};
